import useNavigator from "@dashboard/hooks/useNavigator";
import { Box, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { sectionMessages } from "../../messages";
import {
  wsmDealerCustomerListPath,
  wsmDealerGroupListPath,
  wsmDealerSettingsPath,
} from "../../urls";

export type WsmDealerTab = "groups" | "customers" | "settings";

/**
 * One strip on all three of the section's screens. Settings and Customers used
 * to be reachable only by a button on the group list, which is not a Dashboard
 * pattern and left two screens with no way back to each other. Every tab's label
 * is that screen's own page title, so what you clicked and where you landed read
 * the same.
 */
export const WsmDealerTabs = ({ current }: { current: WsmDealerTab }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const tabs: Array<{ id: WsmDealerTab; label: string; path: string }> = [
    {
      id: "groups",
      label: intl.formatMessage(sectionMessages.groupListTitle),
      path: wsmDealerGroupListPath,
    },
    {
      id: "customers",
      label: intl.formatMessage(sectionMessages.customerListTitle),
      path: wsmDealerCustomerListPath,
    },
    {
      id: "settings",
      label: intl.formatMessage(sectionMessages.settingsTitle),
      path: wsmDealerSettingsPath,
    },
  ];

  return (
    <Box
      display="flex"
      gap={7}
      paddingX={6}
      borderBottomWidth={1}
      borderBottomStyle="solid"
      borderColor="default1"
      data-test-id="wsm-dealer-tabs"
    >
      {tabs.map(tab => {
        const active = tab.id === current;

        return (
          <Box
            key={tab.id}
            paddingY={4}
            style={{
              cursor: "pointer",
              borderBottom: active ? "2px solid currentColor" : "2px solid transparent",
              marginBottom: "-1px",
            }}
            onClick={() => navigate(tab.path)}
            data-test-id={`wsm-dealer-tab-${tab.id}`}
          >
            <Text
              size={3}
              color={active ? "default1" : "default2"}
              fontWeight={active ? "medium" : "regular"}
            >
              {tab.label}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
};

export default WsmDealerTabs;
